import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkle, ShieldCheck, FileText, ChartLine, ArrowRight } from '@phosphor-icons/react';
import { useAuth } from '@/App';

const NaviIntroduction = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const intakeId = location.state?.intakeId;

  const handleGenerateUniverse = () => {
    if (intakeId) {
      navigate('/universe/generating', { state: { intakeId } });
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl w-full"
      >
        {/* Header Section */}
        <div className="text-center mb-12">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring" }}
            className="inline-flex items-center justify-center w-20 h-20 bg-gold rounded-full mb-6"
          >
            <Sparkle size={40} weight="fill" className="text-gold-foreground" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-4xl font-heading font-light text-text-primary mb-3"
          >
            Hello <span className="text-gold font-medium">{user?.company_name || 'there'}</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-xl text-text-secondary"
          >
            I'm <span className="text-gold font-medium">NAVI</span> — your Agentic AI Compliance Officer
          </motion.p>
        </div>

        {/* What is Regulatory Universe */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mb-8 p-8 bg-background-surface border border-border rounded-sm"
        >
          <div className="flex items-start gap-4 mb-4">
            <FileText size={32} className="text-gold flex-shrink-0" weight="light" />
            <div>
              <h2 className="text-2xl font-heading font-medium text-text-primary mb-4">
                What is a Regulatory Universe?
              </h2>
              <p className="text-text-primary leading-relaxed mb-4">
                A <span className="text-gold font-medium">Regulatory Universe</span> is a structured view of all legislation, regulations, and standards applicable to your organisation based on your business activities, industry, and risk exposure.
              </p>
              <p className="text-text-primary leading-relaxed">
                It defines the full scope of your compliance obligations for the financial year and forms the foundation of your compliance programme, risk monitoring, and audit planning.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Governance Rules */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mb-8 p-8 bg-background-surface border border-gold rounded-sm"
        >
          <div className="flex items-start gap-4 mb-6">
            <ShieldCheck size={32} className="text-gold flex-shrink-0" weight="light" />
            <h2 className="text-2xl font-heading font-medium text-text-primary">
              Governance Rules
            </h2>
          </div>

          <div className="space-y-4">
            <div className="pl-12">
              <div className="mb-6">
                <h3 className="text-gold font-semibold mb-2 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-gold text-gold-foreground text-sm flex items-center justify-center font-mono">1</span>
                  Finalisation
                </h3>
                <p className="text-text-primary">
                  Once approved, your Regulatory Universe is <span className="text-gold font-medium">locked for the financial year</span>.
                </p>
              </div>

              <div className="mb-6">
                <h3 className="text-gold font-semibold mb-2 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-gold text-gold-foreground text-sm flex items-center justify-center font-mono">2</span>
                  Amendments
                </h3>
                <p className="text-text-primary">
                  Only <span className="text-gold font-medium">Super Users</span> can amend the Regulatory Universe, and all changes must include a <span className="text-gold font-medium">documented rationale and audit trail</span>.
                </p>
              </div>

              <div>
                <h3 className="text-gold font-semibold mb-3 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-gold text-gold-foreground text-sm flex items-center justify-center font-mono">3</span>
                  Risk-Based Action
                </h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 bg-risk-high-bg border border-risk-high-border rounded-sm">
                    <div className="w-3 h-3 rounded-full bg-risk-high-text flex-shrink-0 mt-1"></div>
                    <div>
                      <div className="text-risk-high-text font-semibold mb-1">High Risk Regulations</div>
                      <div className="text-text-primary text-sm">
                        Require <span className="font-medium">Diagnostics Assessment + Continuous Monitoring</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-risk-medium-bg border border-risk-medium-border rounded-sm">
                    <div className="w-3 h-3 rounded-full bg-risk-medium-text flex-shrink-0 mt-1"></div>
                    <div>
                      <div className="text-risk-medium-text font-semibold mb-1">Medium Risk Regulations</div>
                      <div className="text-text-primary text-sm">
                        Require <span className="font-medium">Diagnostics Assessment</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-risk-low-bg border border-risk-low-border rounded-sm">
                    <div className="w-3 h-3 rounded-full bg-risk-low-text flex-shrink-0 mt-1"></div>
                    <div>
                      <div className="text-risk-low-text font-semibold mb-1">Low Risk Regulations</div>
                      <div className="text-text-primary text-sm">
                        Require <span className="font-medium">Simplified Diagnostics</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="text-center"
        >
          <button
            data-testid="generate-universe-btn"
            onClick={handleGenerateUniverse}
            className="px-10 py-4 bg-gold hover:bg-gold-hover text-gold-foreground text-lg font-medium rounded-sm transition-all inline-flex items-center gap-3 shadow-lg hover:shadow-xl"
          >
            <Sparkle size={24} weight="fill" />
            Generate My Regulatory Universe
            <ArrowRight size={24} weight="bold" />
          </button>
          
          <p className="mt-4 text-sm text-text-tertiary">
            This will take approximately 20-30 seconds
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default NaviIntroduction;
